// Shared styled components extracted from existing chat components
// These are EXACT copies of the existing styles to ensure no visual changes

import styled from "styled-components";
import { Card, Input, Typography } from "antd";
import {colors} from "../../../util/colors.ts";

const { Title } = Typography;

// Main Containers
export const MainContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;

export const ChatLayoutContainer = styled.div`
    display: flex;
    height: 90vh;
    width: 100%;
    gap: 0;
    border-radius: 16px;
    background-color: white;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    overflow: hidden;

    @media (max-width: 991px) {
        height: calc(100vh - 240px);
        max-height: calc(100vh - 240px);
        border-radius: 12px;
        position: relative;
        z-index: 1;
    }
`;

export const ChatContainer = styled.div<{ isExpanded?: boolean; isTransitioning?: boolean; withSidebar?: boolean; sidebarCollapsed?: boolean }>`
    border-radius: 16px;
    background-color: white;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    z-index: 1000;
    height: 90vh;
    overflow: hidden;
    transition: all 0.3s ease;
    transform-origin: bottom right;
    opacity: ${({isTransitioning}) => (isTransitioning ? 0 : 1)};
    transform: ${({isTransitioning}) => (isTransitioning ? 'scale(0.95)' : 'scale(1)')};
    flex: ${({withSidebar}) => (withSidebar ? '1' : 'auto')};
    ${({withSidebar, sidebarCollapsed}) => withSidebar && !sidebarCollapsed && `
        border-radius: 16px 0 0 16px;
        box-shadow: none;
    `}
    ${({withSidebar, sidebarCollapsed}) => withSidebar && sidebarCollapsed && `
        border-radius: 16px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    `}

    @media (max-width: 991px) {
        height: calc(100vh - 240px);
        max-height: calc(100vh - 240px);
        border-radius: 12px;
        position: relative;
        z-index: 1;
    }
`;

// Messages
export const MessagesContainer = styled.div`
    flex: 1;
    padding: 16px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
    scroll-behavior: smooth;
`;

export const MessageWrapper = styled.div<{ sender: "user" | "agent" }>`
    display: flex;
    justify-content: ${({sender}) => (sender === "user" ? "flex-end" : "flex-start")};
    opacity: 0;
    transform: translateY(10px);
    animation: messageAppear 0.3s forwards;

    @keyframes messageAppear {
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

export const MessageBubble = styled.div<{ sender: "user" | "agent" }>`
    max-width: 80%;
    padding: 14px 18px;
    border-radius: ${({sender}) =>
            sender === "user"
                    ? "18px 18px 4px 18px"
                    : "18px 18px 18px 4px"};
    font-size: 15px;
    background-color: ${({sender}) =>
            sender === "user" ? "#F3F4F6" : "transparent"};
    color: ${({sender}) => (sender === "user" ? "#1F2937" : "#1F2937")};
    word-break: break-word;
`;

export const ErrorMessage = styled(MessageBubble)`
    background-color: #FEE2E2;
    color: #B91C1C;
    border-left: 3px solid #B91C1C;
`;

export const TypingIndicator = styled.div`
    align-self: flex-start;
    padding: 8px 16px;
    background-color: #F3F4F6;
    border-radius: 18px;
    font-size: 14px;
    color: #6B7280;
    display: flex;
    align-items: center;
    gap: 8px;
`;

// Input Area
export const InputArea = styled.div`
    display: flex;
    padding: 16px 16px 4px 16px;
    border-top: 1px solid #E5E7EB;
    background-color: white;
`;

export const MessageInput = styled(Input)`
    flex: 1;
    padding: 14px 18px;
    border: 1px solid #D1D5DB;
    border-radius: 24px;
    outline: none;
    font-size: 15px;
    background-color: #F9FAFB;
    transition: all 0.2s ease;

    &:focus {
        border-color: #00a9b2;
        box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        background-color: white;
    }

    &:disabled {
        background-color: #F3F4F6;
        cursor: not-allowed;
    }
`;

export const MessageTextArea = styled(Input.TextArea)`
    flex: 1;
    padding: 14px 18px;
    border: 1px solid #D1D5DB;
    border-radius: 24px;
    outline: none;
    font-size: 15px;
    background-color: #F9FAFB;
    transition: all 0.2s ease;

    &:focus {
        border-color: #00a9b2;
        box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        background-color: white;
    }

    &:disabled {
        background-color: #F3F4F6;
        cursor: not-allowed;
    }
`;

export const SendButton = styled.button`
    background-color: #00a9b2;
    color: white;
    border: none;
    border-radius: 50%;
    width: 46px;
    height: 46px;
    margin-left: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 6px rgba(79, 70, 229, 0.3);

    &:hover {
        background-color: #0075b2;
        transform: translateY(-2px);
    }

    &:active {
        transform: translateY(0);
    }

    &:disabled {
        background-color: #9CA3AF;
        cursor: not-allowed;
        transform: translateY(0);
        box-shadow: none;
    }
`;

export const NewConversationButton = styled.button`
    width: 100%;
    background: linear-gradient(135deg, #00a9b2, #0075b2);
    color: white;
    border: none;
    padding: 14px 18px;
    border-radius: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 500;
    transition: all 0.2s ease;
    box-shadow: 0 2px 6px rgba(0, 169, 178, 0.3);

    &:hover {
        background: linear-gradient(135deg, #0075b2, #00a9b2);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 169, 178, 0.4);
    }

    &:active {
        transform: translateY(0);
    }
`;

// AI Jobs specific components (extracted from AgentChat.tsx)
export const JobsListContainer = styled.div<{ isTransitioning?: boolean }>`
    border-radius: 16px;
    background-color: ${colors.bg};
    display: flex;
    flex-direction: column;
    z-index: 1000;
    transition: all 0.5s ease;
    opacity: ${({isTransitioning}) => (isTransitioning ? 0 : 1)};
    transform: ${({isTransitioning}) => (isTransitioning ? 'scale(0.95)' : 'scale(1)')};
`;

export const JobsHeader = styled.div`
    padding: 20px 24px;
    border-bottom: 1px solid #E5E7EB;
    background-color: #F9FAFB;
    display: flex;
    align-items: center;
    gap: 16px;
`;

export const BackButton = styled.button`
    background: none;
    border: 1px solid #D1D5DB;
    color: #6B7280;
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;
    font-size: 14px;

    &:hover {
        background-color: #F3F4F6;
        border-color: #9CA3AF;
        color: #374151;
    }
`;

export const JobsTitle = styled.div`
    font-size: 20px;
    font-weight: 600;
    color: #1F2937;
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const JobsGrid = styled.div`
    flex: 1;
    padding: 24px;
    overflow-y: auto;
`;

export const JobCard = styled(Card)`
    height: 100%;
    display: flex;
    flex-direction: column;
    animation: slideInUp 0.6s ease forwards;
    opacity: 0;
    transform: translateY(20px);

    @keyframes slideInUp {
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .ant-card-body {
        padding: 20px;
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    &:hover {
        transform: translateY(-4px);
    }
`;

export const JobCardContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const JobHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
`;

export const JobTitle = styled(Title)`
    margin: 0 !important;
    font-size: 16px !important;
    line-height: 1.3 !important;
`

export const LocationInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    color: #6B7280;
`;

export const SalaryInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    color: #059669;
`;

export const JobCategory = styled.div`
    margin: 4px 0;
`;

export const JobDescription = styled.div`
    margin: 8px 0;
    line-height: 1.5;
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const RequirementsSection = styled.div`
    margin-top: auto;
    padding-top: 12px;
`;

export const RequirementsList = styled.ul`
    margin: 8px 0 0 0;
    padding-left: 16px;
    font-size: 13px;
    color: #6B7280;

    li {
        margin-bottom: 4px;
    }
`;

export const JobActions = styled.div`
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const DeleteButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    transition: all 0.2s ease;

    &:hover {
        transform: scale(1.2);
    }
`;

export const StoredJobsButton = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    background: linear-gradient(135deg, #00a9b2, #0075b2);
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: all 0.2s ease;
    z-index: 10;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    &:active {
        transform: translateY(0);
    }
`;

// Job Coach specific components (extracted from JobCoachChat.tsx)
export const RecommendationsContainer = styled.div`
    margin: 16px 0;
    padding: 16px;
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    border-radius: 16px;
    border: 1px solid #e2e8f0;
`;

export const RecommendationsHeader = styled.h3`
    margin: 0 0 12px 0;
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const RecommendationsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const RecommendationItem = styled.div`
    background: white;
    padding: 12px 16px;
    border-radius: 12px;
    border-left: 4px solid #00a9b2;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    font-size: 14px;
    line-height: 1.5;
`;

// Legacy recommendation components (from AgentChat.tsx)
export const LegacyRecommendationsContainer = styled.div`
    margin-top: 4px;
    padding: 14px 16px;
    background-color: #F9FAFB;
    border-radius: 12px;
    border-left: 4px solid #4F46E5;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    animation: slideIn 0.5s forwards;

    @keyframes slideIn {
        from {
            opacity: 0;
            transform: translateY(10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

export const LegacyRecommendationTitle = styled.div`
    font-weight: 600;
    margin-bottom: 10px;
    color: #4F46E5;
    display: flex;
    align-items: center;
    gap: 6px;
`;

export const LegacyRecommendationItem = styled.div`
    margin-bottom: 8px;
    padding: 8px 10px;
    background-color: white;
    border-radius: 8px;
    font-size: 14px;
    border: 1px solid #E5E7EB;
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
        border-color: #4F46E5;
        background-color: #F5F5FF;
    }

    strong {
        color: #4F46E5;
    }
`;
